import type { GraphQLError } from 'graphql/error';
import { gql, GraphQLClient } from 'graphql-request';
import { IncomingMessage } from 'http';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GQLQueryVariables = Record<string, any>;
/**
 * These are our GraphQL endpoints for the different environments. We have 2 categories:
 *    1. Browser - called from the public internet, gets auth and other request context processed by pitstop. Called via browser
 *    2. Server - These *_GQL_SERVER_ENDPOINT variables are ONLY called on our private internet/ server side NextJS.
 *          * allows the traffic to stay internal to our network, saving us money and latency
 *          * allows us to forward x-strava-* headers so we can rate limit based on the true client IP as
 *            opposed to the IP of our Kubernetes node that the web server is running on.
 *
 * Each category has a url that points to a different graph variant. Client, staging, or current.
 * Check out the confluence page for more info https://strava.atlassian.net/l/cp/0PSj241M
 */

// endpoints to be called on the client side of the browser
export const CLIENTS_GQL_ENDPOINT = 'https://graphql.strava.com';
export const STAGING_GQL_ENDPOINT = 'https://graphql.staging.strava.com';
export const STAGING_ADMIN_GQL_ENDPOINT =
  'https://www.staging.strava.com/admin/graphql';
export const CURRENT_ADMIN_GQL_ENDPOINT =
  'https://admin.strava.com/admin/graphql';

// internal router endpoints to only be called within our network. Used on Server Side NextJS
export const CLIENTS_GQL_SERVER_ENDPOINT =
  'http://prod.router.graphql.discovery.svc.strava.com/';
export const STAGING_GQL_SERVER_ENDPOINT =
  'http://staging.router.graphql.discovery.svc.strava.com/';

export const getGraphEndpoint = (
  isAdmin: boolean,
  isStaging: boolean,
  isServerSide: boolean
) => {
  // admin does not differentiate between server or browser
  if (isAdmin) {
    return isStaging ? STAGING_ADMIN_GQL_ENDPOINT : CURRENT_ADMIN_GQL_ENDPOINT;
  }
  // if we are on the server, use the internal endpoints (reasoning above)
  if (isServerSide) {
    return isStaging
      ? STAGING_GQL_SERVER_ENDPOINT
      : CLIENTS_GQL_SERVER_ENDPOINT;
  }
  return isStaging ? STAGING_GQL_ENDPOINT : CLIENTS_GQL_ENDPOINT;
};

// this is the shape of the request object in GetServerSidePropsContext
export interface StravaGQLRequest extends IncomingMessage {
  cookies: Partial<Record<string, string>>;
}

/**
 * We want to also return the status code and
 * any graphql error messages to be consumed
 * downstream by the callers.
 */
export interface StravaGQLError extends Error {
  statusCode: number;
  errors?: GraphQLError[];
}

/**
 * gets cookies from request.
 * @param {Object} req - request object from getServerSideProps
 * @returns the constructed auth cookie string
 */
export const getCookies = (req: StravaGQLRequest) => {
  const cookies = req.headers && req.headers.cookie ? req.headers.cookie : '';
  return cookies;
};

/**
 * Returns object containing all x-strava-* headers from the req.headers
 * @param {object} req - request object from getServerSideProps
 * @returns {object} - Object containing all x-strava-* headers or empty object when there are not matches
 */
export const getStravaHeaders = (req: StravaGQLRequest) =>
  Object.keys(req?.headers ?? {})
    .filter((key) => /x-strava-/.test(key.toLowerCase()))
    .reduce(
      (acc, key) => {
        acc[key] = req.headers[key as keyof typeof req.headers] as string;
        return acc;
      },
      {} as Record<string, string>
    );

const buildErrorFromErrors = (errors: GraphQLError[], status: number) => {
  const extensions = errors?.[0]?.extensions ?? {};
  const extensionErrorMessage = extensions.message as string | undefined;
  const errorMessage = errors?.[0]?.message;
  const message =
    extensionErrorMessage || errorMessage || `GraphQL Error (Code: ${status})`;
  const error = new Error(message) as StravaGQLError;
  error.statusCode = 500;
  error.errors = errors;

  return error;
};

const buildErrorFromStatus = (status: number) => {
  const error = new Error(`GraphQL Error (Code: ${status})`) as StravaGQLError;
  error.statusCode = status;

  return error;
};

const buildErrorFromGenericError = (unknownError: unknown) => {
  let message = '';
  if (unknownError instanceof Error) {
    message = unknownError.message;
  } else {
    message = String(unknownError);
  }
  const error = new Error(message) as StravaGQLError;
  error.statusCode = 500;
  return error;
};

/**
 * use the built graphql client with its given configuration and parameters to call the graphql endpoint and return the data or error.
 * @param {GraphQLClient} gqlClient - the GQL client built with the desired configurations
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the response object from the graphql request
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
const callWithClient = <TResult, TVariables = GQLQueryVariables>(
  gqlClient: GraphQLClient,
  query: TypedDocumentNode<TResult, TVariables> | string,
  variables?: GQLQueryVariables
): Promise<TResult> => {
  const data = gqlClient.request(query, variables).catch((clientError) => {
    /**
     * GraphQLClient JS Error shape example:
     ClientError: 'sampleMessage': {
     response: {
     errors: [ {message: "sampleMessage", extensions:{code:"FORBIDDEN"} ],
     status: 500,
     headers: Headers {}
     },
     request: {
     query:
     query Query {},
     variables: {}
     }
     }
     */

    const { response } = clientError;
    if (response) {
      if (response.status >= 200 && response.status <= 299) {
        // A 2XX error response indicates the caller needs to investigate
        // the error array.  See https://www.apollographql.com/docs/apollo-server/data/errors
        // This means it was not an operational error from Graphql.
        throw buildErrorFromErrors(response.errors, response.status);
      } else {
        throw buildErrorFromStatus(response.status);
      }
    } else {
      throw buildErrorFromGenericError(clientError);
    }
  });
  return data;
};

// The 'apollographql-client-name' request header is passed in with all
// client and server side requests. It allows us to accurately attribute
// graphql requests as having come from our various web apps.
// https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup
export const DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME = 'strava-web';
export const getApolloGraphqlClientName = () => {
  // Check process.env.APOLLO_GRAPHQL_CLIENT_NAME to see if a unique client
  // name is defined. If not, fall back to DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME.
  // This is wrapped in a try/catch so that in the case where process is not
  // defined, we will ignore the "process is not defined" error to keep it
  // from bubbling up to the client and blocking requests and just return
  // the default client name.
  try {
    const clientName = process.env.APOLLO_GRAPHQL_CLIENT_NAME;
    return clientName ?? DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME;
  } catch {
    // process isn't defined, ignore error
  }

  return DEFAULT_APOLLO_GRAPHQL_CLIENT_NAME;
};

const extractGraphQLEndpoint = (reqHost: string, isAdmin = false) => {
  // call staging endpoint for localhost and staging
  // webapp is included in this list for when we run the app using docker-compose and for Cypress tests
  const useStagingGql = ['staging', 'localhost', 'webapp', '::1'].some(
    (host) => reqHost?.includes(host) // if reqHost is undefined useStaging gql will be false
  );

  const useNextProxyEndpoint = ['localhost'].some((host) =>
    reqHost?.includes(host)
  );

  // if on NextJS local development on the server side, use the public endpoints
  // we do not get strava headers on local development since we hit the nextjs local server
  // instead of through the strava servers.
  let useServerSideEndpoints = true;
  if (process.env.NODE_ENV === 'development') {
    useServerSideEndpoints = false;
  }

  let endpoint = getGraphEndpoint(
    isAdmin,
    useStagingGql,
    useServerSideEndpoints
  );

  // when developing locally, we need to use the dev-proxy
  if (process.env.NODE_ENV === 'development' && useNextProxyEndpoint) {
    endpoint = `http://${
      reqHost ? encodeURI(reqHost) : 'localhost:3000'
    }/dev-proxy/${isAdmin ? 'admin-' : ''}graphql`;
  }

  return endpoint;
};

const serverSideRequest = <TResult, TVariables = GQLQueryVariables>(
  req: StravaGQLRequest,
  query: TypedDocumentNode<TResult, TVariables> | string,
  endpoint: string,
  variables?: GQLQueryVariables
): Promise<TResult> => {
  const stravaHeaders = getStravaHeaders(req);
  const gqlClient = new GraphQLClient(endpoint, {
    headers: {
      Cookie: getCookies(req),
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName(),
      ...stravaHeaders
    }
  });
  return callWithClient(gqlClient, query, variables);
};

export const buildClient = (
  req: StravaGQLRequest,
  endpoint: string
): GraphQLClient => {
  const stravaHeaders = getStravaHeaders(req);
  return new GraphQLClient(endpoint, {
    headers: {
      Cookie: getCookies(req), //the session graph requires a session id to be passed in and
      //we indirectly do this now by passing in a session cookie, we
      //should investigate passing this value in the variables instead of
      //at this level.
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName(),
      ...stravaHeaders
    }
  });
};

// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.

const clientSideRequest = <TResult, TVariables = GQLQueryVariables>(
  query: TypedDocumentNode<TResult, TVariables> | string,
  variables?: GQLQueryVariables,
  isAdmin = false
): Promise<TResult> => {
  const { location } = window;

  const hostname = location.hostname;
  // This addition is a temporary fix to enabled community management to view
  // the admin graphql data on admin.strava.com. Web Platform is having talks with
  // integrity and others to determine the proper way to handle admin feature access
  // see the PR description for more info
  if (!isAdmin && hostname.match(/(admin)/g)) {
    return gqlAdminRequest(query, variables);
  }
  const useStagingGql = ['staging', 'localhost', '::1'].some((host) =>
    hostname.includes(host)
  );

  const isServerSide = false;
  let endpoint = getGraphEndpoint(isAdmin, useStagingGql, isServerSide);

  const useNextProxyEndpoint = ['localhost'].some(
    (host) =>
      typeof window !== 'undefined' && window.location.hostname.includes(host)
  );
  // when developing locally, we need to use the dev-proxy
  // middleware to prevent CORS on the client side.
  // We only want to use the dev-proxy endpoint for Next apps. Using this
  // endpoint to run MFEs locally will not work.
  if (process.env.NODE_ENV === 'development' && useNextProxyEndpoint) {
    endpoint = `http://${window.location.host}/dev-proxy/${
      isAdmin ? 'admin-' : ''
    }graphql`;
  }

  const graphQLClient = new GraphQLClient(endpoint, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'apollographql-client-name': getApolloGraphqlClientName()
    }
  });

  return callWithClient(graphQLClient, query, variables);
};

/**
 * Makes a GraphQL request to internal admin graph using the req object passed in from getServerSideProps
 * this is needed since the include credentials flag only works on the client side
 * should only be used when the call is made on the server side through getServerSideProps in NextJS.
 * @param {Object} req - the request object from getServerSideProps
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.

export const gqlAdminServerSideRequest = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TResult = any,
  TVariables = GQLQueryVariables
>(
  req: StravaGQLRequest,
  query: TypedDocumentNode<TResult, TVariables> | string,
  variables?: GQLQueryVariables
): Promise<TResult> => {
  const reqHost = req.headers.host || '';
  const endpoint = extractGraphQLEndpoint(reqHost, true);
  return serverSideRequest(req, query, endpoint, variables);
};

/**
 * Makes a GraphQL request using the req object passed in from getServerSideProps
 * this is needed since the include credentials flag only works on the client side
 * should only be used when the call is made on the server side through getServerSideProps in NextJS.
 * @param {Object} req - the request object from getServerSideProps
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @param {string} withEndpoint - allows caller to specify graphQL endpoint to use instead of extracted endpoint from environment variables.
 * @returns the graphql response retrieved with the given query and auth information
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.

export const gqlServerSideRequest = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TResult = any,
  TVariables = GQLQueryVariables
>(
  req: StravaGQLRequest,
  query: TypedDocumentNode<TResult, TVariables> | string,
  variables?: GQLQueryVariables,
  withEndpoint = ''
): Promise<TResult> => {
  const beginsWithHttp = withEndpoint.startsWith('http');
  const reqHost = req.headers.host || '';
  const endpoint = beginsWithHttp ? withEndpoint : extractGraphQLEndpoint(reqHost);
  return serverSideRequest(req, query, endpoint, variables);
};

/**
 * Calls the internal admin graph if the user has the proper credentials and if it is on admin.strava.com + behind VPN
 * Perfect for admin microfrontends
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query and auth information
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gqlAdminRequest = <TResult = any, TVariables = GQLQueryVariables>(
  query: TypedDocumentNode<TResult, TVariables> | string,
  variables?: GQLQueryVariables
): Promise<TResult> => {
  return clientSideRequest(query, variables, true);
};

/**
 * Makes a graphql request on the client.
 * Infers the credentials from the browser to authenticate.
 * Perfect for athlete facing microfrontends
 * @param {string} query - the graphql query string
 * @param {Object} variables - the additional graphql variables to be passed
 * @returns the graphql response retrieved with the given query
 */
// Set default generic type to any, so it can be used without explicit typing. Existing code that don't use generics still work.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const gqlRequest = <TResult = any, TVariables = GQLQueryVariables>(
  query: TypedDocumentNode<TResult, TVariables> | string,
  variables?: GQLQueryVariables
): Promise<TResult> => {
  return clientSideRequest(query, variables, false);
};

export { gql };
